import axiosInstance from './axiosInstance';

export interface AmsProjectAsset {
  _id: string;
  id?: string; // toJSON-transformed responses expose 'id'
  name: string;
  description?: string;
  code?: string;
  masterAssetId: string | { _id: string; name?: string };
  amsProjectId: string;
  amsProjectAuditId?: string;
  status: 'active' | 'inactive';
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

class AmsProjectAssetService {
  /**
   * Create or get AMS project asset
   */
  async createOrGetAmsProjectAsset(
    amsProjectId: string,
    amsProjectAuditId: string,
    masterAssetId: string,
    selectedCheckpointIds?: string[]
  ): Promise<AmsProjectAsset> {
    const response = await axiosInstance.post('/ams/ams-project-assets', {
      amsProjectId,
      amsProjectAuditId,
      masterAssetId,
      selectedCheckpointIds,
    });
    return response.data.data;
  }

  /**
   * Get AMS project assets by project
   */
  async getAmsProjectAssetsByProject(amsProjectId: string): Promise<AmsProjectAsset[]> {
    const response = await axiosInstance.get('/ams/ams-project-assets/by-project', {
      params: { amsProjectId },
    });
    return response.data.data || [];
  }

  /**
   * Get AMS project assets for a specific audit
   */
  async getAmsProjectAssetsByAudit(amsProjectAuditId: string): Promise<AmsProjectAsset[]> {
    const response = await axiosInstance.get('/ams/ams-project-assets/by-audit', {
      params: { amsProjectAuditId },
    });
    return response.data.data || [];
  }

  /**
   * Get AMS project asset by ID
   */
  async getAmsProjectAssetById(amsProjectAssetId: string): Promise<AmsProjectAsset> {
    const response = await axiosInstance.get(`/ams/ams-project-assets/${amsProjectAssetId}`);
    return response.data.data;
  }

  /**
   * Get AMS project asset by project and master asset
   */
  async getAmsProjectAssetByProjectAndMasterAsset(
    amsProjectId: string,
    masterAssetId: string
  ): Promise<AmsProjectAsset | null> {
    const response = await axiosInstance.get('/ams/ams-project-assets/by-project-and-master', {
      params: { amsProjectId, masterAssetId },
    });
    return response.data.data;
  }

  /**
   * Update AMS project asset
   */
  async updateAmsProjectAsset(
    amsProjectAssetId: string,
    updateData: Partial<AmsProjectAsset>
  ): Promise<AmsProjectAsset> {
    const response = await axiosInstance.patch(
      `/ams/ams-project-assets/${amsProjectAssetId}`,
      updateData
    );
    return response.data.data;
  }

  /**
   * Delete AMS project asset (soft delete)
   */
  async deleteAmsProjectAsset(amsProjectAssetId: string): Promise<void> {
    await axiosInstance.delete(`/ams/ams-project-assets/${amsProjectAssetId}`);
  }
}

const amsProjectAssetService = new AmsProjectAssetService();
export default amsProjectAssetService;
